import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

function onlyDigits(s) {
  return (s || '').replace(/\D/g, '');
}

function formatCEP(s) {
  const d = onlyDigits(s).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Reusable structured-address fields with ViaCEP autofill.
 * Controlled component: pass `value` (object with address_* keys) and `onChange(next)`.
 *
 * Keys expected:
 *  address_cep, address_street, address_number, address_neighborhood,
 *  address_city, address_state, address_complement
 */
export function AddressFields({ value, onChange, prefix = '', testIdPrefix = 'addr' }) {
  const [busy, setBusy] = useState(false);
  const v = value || {};

  const set = (k, val) => onChange({ ...v, [k]: val });

  const lookupCEP = async (raw) => {
    const cep = onlyDigits(raw);
    if (cep.length !== 8) return;
    setBusy(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data && !data.erro) {
        onChange({
          ...v,
          address_cep: formatCEP(cep),
          address_street: data.logradouro || v.address_street || '',
          address_neighborhood: data.bairro || v.address_neighborhood || '',
          address_city: data.localidade || v.address_city || '',
          address_state: data.uf || v.address_state || '',
        });
      }
    } catch (e) {
      // silent fail; user can fill manually
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
      <div className="sm:col-span-4">
        <Label>CEP</Label>
        <Input
          value={formatCEP(v.address_cep || '')}
          onChange={(e) => {
            const raw = e.target.value;
            set('address_cep', formatCEP(raw));
            const digits = onlyDigits(raw);
            if (digits.length === 8) lookupCEP(digits);
          }}
          onBlur={(e) => lookupCEP(e.target.value)}
          placeholder="00000-000"
          inputMode="numeric"
          maxLength={9}
          data-testid={`${testIdPrefix}-cep`}
          disabled={busy}
        />
        {busy && <div className="text-xs text-muted-foreground mt-1">Buscando CEP…</div>}
      </div>
      <div className="sm:col-span-6">
        <Label>Logradouro</Label>
        <Input
          value={v.address_street || ''}
          onChange={(e) => set('address_street', e.target.value)}
          placeholder="Rua, avenida, etc."
          data-testid={`${testIdPrefix}-street`}
        />
      </div>
      <div className="sm:col-span-2">
        <Label>Número</Label>
        <Input
          value={v.address_number || ''}
          onChange={(e) => set('address_number', e.target.value)}
          placeholder="Nº"
          data-testid={`${testIdPrefix}-number`}
        />
      </div>
      <div className="sm:col-span-4">
        <Label>Bairro</Label>
        <Input
          value={v.address_neighborhood || ''}
          onChange={(e) => set('address_neighborhood', e.target.value)}
          data-testid={`${testIdPrefix}-neighborhood`}
        />
      </div>
      <div className="sm:col-span-5">
        <Label>Cidade</Label>
        <Input
          value={v.address_city || ''}
          onChange={(e) => set('address_city', e.target.value)}
          data-testid={`${testIdPrefix}-city`}
        />
      </div>
      <div className="sm:col-span-3">
        <Label>Estado</Label>
        <Input
          value={v.address_state || ''}
          onChange={(e) => set('address_state', e.target.value.toUpperCase().slice(0, 2))}
          placeholder="UF"
          maxLength={2}
          data-testid={`${testIdPrefix}-state`}
        />
      </div>
      <div className="sm:col-span-12">
        <Label>Complemento</Label>
        <Input
          value={v.address_complement || ''}
          onChange={(e) => set('address_complement', e.target.value)}
          placeholder="Apto, sala, ponto de referência, etc."
          data-testid={`${testIdPrefix}-complement`}
        />
      </div>
    </div>
  );
}

export const ADDRESS_KEYS = [
  'address_cep',
  'address_street',
  'address_number',
  'address_neighborhood',
  'address_city',
  'address_state',
  'address_complement',
];

export function pickAddress(obj, prefix = '') {
  const out = {};
  for (const k of ADDRESS_KEYS) {
    out[k] = obj?.[prefix + k] || '';
  }
  return out;
}

export function spreadAddress(addr, prefix = '') {
  const out = {};
  for (const k of ADDRESS_KEYS) {
    out[prefix + k] = addr?.[k] || null;
  }
  return out;
}
